import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import { Check, Star, Zap, X, Loader2, RefreshCw, Info } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useWalletKit } from "@web3jskit/walletkit";
import { MaxUint256, parseUnits } from "ethers";
import {
  subscribePlan,
  changeSubscription,
  createDepositOrder,
  getSubscriptionOrders,
  getDepositOrderDetail,
  type SubscriptionState,
  type SubscriptionBillingCycle,
} from "@/api";
import { InfoTooltip } from "../components/ui/InfoTooltip";
import { useAuthStore } from "../../stores/modules/auth";
import {
  useDepositBlockchainsStore,
  selectPrimaryBlockchain,
} from "../../stores/modules/depositBlockchains";
import {
  useSubscriptionPlansList,
  useSubscriptionPlansLoading,
  useSubscriptionPlansLoaded,
  useRefreshSubscriptionPlans,
} from "../../stores/modules/plans";
import {
  useCurrentSubscription,
  useCurrentSubscriptionLoading,
  useCurrentSubscriptionLoaded,
  useRefreshCurrentSubscription,
} from "../../stores/modules/currentSubscription";
import { useWalletSignature } from "../hooks/useWalletSignature";
import { hasValidChainLogoUrl } from "../components/NetworkInfoCard";
import { formatUsdtBalance } from "@/utils";
import {
  clampNonNegative,
  formatSubscriptionPlanPriceDisplay,
  isPositiveUsdt,
  ltUsdt,
  lteUsdt,
  resolveSubscriptionPlanPriceUsdt,
  subUsdt,
  tryM,
} from "@/utils/money";
import {
  useDashboardStore,
  useRefreshOverview,
} from "../../stores/modules/dashboard";
import { PlanCardSkeleton } from "../components/ui/skeleton";
import { depositInterface, erc20Interface } from "@/abi";
import { switchOrAddChain } from "@/utils/evmChains";
import {
  WalletError,
  classifyWalletError,
  getWalletErrorMessageKey,
  probeWalletAlive,
  raceWithAbort,
} from "@/utils/walletError";

type PlanId = string;

const AMOUNT_PATTERN = /^\d+(\.\d{1,6})?$/;

/** approve 交易上链轮询间隔（毫秒） */
const RECEIPT_POLL_INTERVAL_MS = 3_000;
/** approve 交易上链轮询最大次数（60s，与 RechargeModal 一致） */
const RECEIPT_POLL_MAX_TIMES = 20;

const DEPOSIT_FLOW = "[DepositFlow]";

function shortHexAddr(addr: string): string {
  if (!addr || addr.length < 12) return addr || "—";
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/**
 * 通过 eth_getTransactionReceipt 轮询等待交易上链。
 * 成功返回 receipt，reverted / 超时抛错；signal abort 时抛 cancelled。
 */
async function waitForReceipt(
  provider: {
    request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  },
  txHash: string,
  signal: AbortSignal
): Promise<{ status: string }> {
  for (let i = 0; i < RECEIPT_POLL_MAX_TIMES; i += 1) {
    if (signal.aborted) throw new WalletError("cancelled");
    const receipt = (await raceWithAbort(
      provider.request({
        method: "eth_getTransactionReceipt",
        params: [txHash],
      }),
      signal
    )) as { status: string } | null;

    if (receipt) {
      if (receipt.status === "0x1") return receipt;
      throw new Error(`Transaction reverted: ${txHash}`);
    }

    await new Promise((resolve) =>
      setTimeout(resolve, RECEIPT_POLL_INTERVAL_MS)
    );
  }
  throw new Error(`Transaction receipt timeout: ${txHash}`);
}

/**
 * Unix 秒 → "YYYY-MM-DD HH:mm[:ss]"；非法值回退为 "—"。
 *
 * 供降级标签展示 `下周期生效（{date}）` 使用；与 Dashboard 的同名 helper
 * 行为一致，本文件与 Dashboard 各维护一份，避免为两处引用提早抽 utils。
 */
function formatDateTime(unixSec: number, withSeconds = true): string {
  if (!unixSec || !Number.isFinite(unixSec)) return "—";
  const d = new Date(unixSec * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}${
    withSeconds ? `:${pad(d.getSeconds())}` : ""
  }`;
  return `${date} ${time}`;
}

/**
 * 订阅变更类型（前端按 subscriptionState + 价格大小派生）
 *
 * - `subscribe`：用户当前为免费计划（`free_active` / `free_past_due`），
 *   走 POST /subscriptions/subscribe 接口
 * - `upgrade`：付费用户切到更高档（目标价 > 当前价），调 change，立即生效 + 积分继承
 * - `downgrade`：付费用户切到更低档付费（目标价 < 当前价 但 isFree=false），下周期生效
 * - `downgrade_to_free`：付费用户切到 Free 计划，下周期生效，不扣款
 */
type ChangeKind =
  | "subscribe"
  | "upgrade"
  | "downgrade"
  | "downgrade_to_free";

/**
 * 订阅支付全流程的 step 枚举。
 *
 * 驱动单个"进度 modal"的标题/描述/是否可关闭，替代原先按互斥 boolean（preparing
 * / signatureLoading / waitStage）控制多个 modal 的方案。无论钱包是静默签名 provider
 * （web3jskit session key）还是标准 EIP-1193 钱包（MetaMask 等），都能给用户一条清晰
 * 的进度路径。
 */
type FlowStep =
  | "idle"
  | "refreshingBalance"
  | "creatingOrder"
  | "switchingChain"
  | "checkingAllowance"
  | "approving"
  | "approveConfirming"
  | "submittingDeposit"
  | "depositSubmitted"
  | "signingSubscription"
  | "callingSubscription"
  | "waitingSubscription";

/**
 * 是否允许用户在当前 step 上主动关闭进度 modal。
 * 仅长轮询阶段（depositSubmitted / waitingSubscription）给用户"稍后再看"的出口；
 * 签名 / RPC / 切链等短阶段不开放关闭，避免误操作中断链上交易。
 */
function isDismissibleStep(step: FlowStep): boolean {
  return step === "depositSubmitted" || step === "waitingSubscription";
}

/**
 * 当前 step 是否处于"等待钱包响应"的交互阶段（切链 / 授权 / 发交易 / 订阅签名）。
 * 这些阶段若因扩展被禁用而永久挂起，需要给用户一个"取消"逃生口去中断流程。
 */
function isWalletWaitingStep(step: FlowStep): boolean {
  return (
    step === "creatingOrder" ||
    step === "switchingChain" ||
    step === "checkingAllowance" ||
    step === "approving" ||
    step === "approveConfirming" ||
    step === "submittingDeposit" ||
    step === "signingSubscription"
  );
}

/**
 * 推导本次订阅操作的种类。
 *
 * 设计上不依赖 yaml 中并未暴露的 sortOrder 字段，
 * 而是用 `monthlyPriceUsdtMicro` 数值大小做升降级比较——价格越高视为档位越高，
 * 与用户直觉一致；同时单独走 free 分支避免「目标价=0」的特殊情况误判为 downgrade。
 */
function deriveChangeKind(
  state: SubscriptionState | undefined,
  currentMicro: number,
  targetMicro: number,
  targetIsFree: boolean
): ChangeKind {
  if (state === "free_active" || state === "free_past_due") {
    return "subscribe";
  }
  if (targetIsFree) return "downgrade_to_free";
  return targetMicro > currentMicro ? "upgrade" : "downgrade";
}

/**
 * 按 changeKind 选择对应的成功 toast 文案 key。
 * 升级文案带「积分继承」提示，与产品需求一致。
 */
function successToastKey(
  kind: ChangeKind,
  t: (k: string) => string
): string {
  switch (kind) {
    case "upgrade":
      return t("recharge.toastUpgradeOk");
    case "downgrade":
    case "downgrade_to_free":
      return t("recharge.toastDowngradeOk");
    case "subscribe":
    default:
      return t("recharge.toastSubscribeOk");
  }
}

export function Recharge() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userInfo = useAuthStore((state) => state.userInfo);
  const { provider } = useWalletKit();
  const { getWalletSignature } = useWalletSignature();
  const primaryChain = useDepositBlockchainsStore(selectPrimaryBlockchain);

  const [billingCycle, setBillingCycle] =
    useState<SubscriptionBillingCycle>("monthly");

  // Plans 来自全局缓存（usePlansStore），由 useAuthedBootstrap 在登录态进入 Layout
  // 时拉取一次；plans 在前端只读，subscribe / cancel 都不会改变 plans 本身。
  const subscriptionPlans = useSubscriptionPlansList();
  const subscriptionPlansLoading = useSubscriptionPlansLoading();
  const subscriptionPlansLoaded = useSubscriptionPlansLoaded();
  const refreshPlans = useRefreshSubscriptionPlans();
  const hasYearlyPlans = useMemo(
    () => subscriptionPlans.some((plan) => plan.yearlyEnabled),
    [subscriptionPlans]
  );

  // currentSubscription 提升到全局持久化 store，避免进入页面时"当前计划"短暂
  // 从 null 起步导致 Basic 卡片可点击的 UX 抖动。
  const currentSubscription = useCurrentSubscription();
  const isLoadingCurrentSubscription = useCurrentSubscriptionLoading();
  const currentSubscriptionLoaded = useCurrentSubscriptionLoaded();
  const refreshCurrentSubscription = useRefreshCurrentSubscription();

  // 首次拉 plans 或 current subscription 任一未到位时显示 skeleton，避免 plans
  // 抢先渲染出来后 currentUserPlan 才异步填充、再把 Basic 标为 disabled 的跳变。
  const isLoadingPlans =
    (subscriptionPlansLoading && !subscriptionPlansLoaded) ||
    (isLoadingCurrentSubscription && !currentSubscriptionLoaded);

  const [userBalance, setUserBalance] = useState<number>(0);

  const [selectedPlanId, setSelectedPlanId] = useState<PlanId | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // 余额刷新中：开支付弹窗时后台 refresh，余额行旁边显示小 spinner，避免显示 stale 值
  const [isRefreshingBalance, setIsRefreshingBalance] = useState(false);
  /** 确认弹窗：首次拉取余额完成后再展示余额区与主按钮文案（避免与顶栏/ Dashboard 不一致） */
  const [isPaymentModalBalanceReady, setIsPaymentModalBalanceReady] =
    useState(false);
  // 充值订阅全流程的当前阶段。替代原先的 isPreparingTx / isSignatureLoading /
  // waitStage 三个互斥 state，给用户一个连贯的进度条式 UI。
  //
  // - 'idle'                ：无进行中流程
  // - 'refreshingBalance'   ：调 refreshOverview 拉最新余额
  // - 'creatingOrder'       ：POST /deposit/orders 创建充值订单
  // - 'switchingChain'      ：钱包切链（可能弹 wallet_switchEthereumChain）
  // - 'checkingAllowance'   ：eth_call 查 ERC20 allowance
  // - 'approving'           ：eth_sendTransaction approve（等钱包签名）
  // - 'approveConfirming'   ：approve 已广播，等链上 receipt
  // - 'submittingDeposit'   ：eth_sendTransaction deposit（等钱包签名）
  // - 'depositSubmitted'    ：deposit 已广播，轮询 /deposit/orders/{id} 等到账
  // - 'signingSubscription' ：/wallet/challenge 签名（订阅授权，不涉及链上交易）
  // - 'callingSubscription' ：subscribe / change 接口 in-flight
  // - 'waitingSubscription' ：pending_order 兜底，轮询订阅订单到 paid
  const [flowStep, setFlowStep] = useState<FlowStep>("idle");
  // 等待 deposit 期间需要展示的金额（USDT），用于差化文案
  const [waitDepositAmount, setWaitDepositAmount] = useState<string>("0");
  // 轮询取消标志：关闭等待弹窗 / 组件卸载 / 到达终态时置 true，循环下一次迭代前会检查并退出
  const cancelPollRef = useRef<boolean>(false);
  // 交互式钱包步骤（切链 / 授权 / 发交易 / 订阅签名）的中断器：用户在等待弹窗点
  // "取消"时 abort，让被禁用扩展导致永久挂起的请求能复位 UI、恢复操作。
  const abortRef = useRef<AbortController | null>(null);

  const currentPlanIdStr = currentSubscription?.planId
    ? String(currentSubscription.planId)
    : null;
  // 是否存在下周期生效的待生效计划（pendingPlan）。yaml Subscription.subscriptionState
  // 为 "paid_pending_change" 时代表当前计费周期结束后会切换到 pendingPlan，本周期内
  // 不允许再次变更订阅计划。前端以此为唯一判定依据，统一禁用非当前的 plan 卡。
  const hasPendingChange =
    currentSubscription?.subscriptionState === "paid_pending_change";
  // 当前付费 plan 的 monthlyPriceUsdtMicro（找不到时按 0 处理；free_active 状态下天然为 0）
  // 注意：currentSubscription 没有 monthlyPriceUsdtMicro 字段，需要从 plans 列表里反查
  const currentPriceMicro = useMemo(() => {
    if (!currentPlanIdStr) return 0;
    const plan = subscriptionPlans.find(
      (p) => String(p.id) === currentPlanIdStr
    );
    // Decimal-safe parse: monthlyPriceUsdtMicro is an integer micro-USDT value;
    // tryM tolerates string / number / null and rejects NaN / Infinity.
    const d = tryM(plan?.monthlyPriceUsdtMicro as number | string | undefined);
    return d === null ? 0 : d.toNumber();
  }, [subscriptionPlans, currentPlanIdStr]);

  const refreshOverview = useRefreshOverview();

  // 拉取当前订阅；余额与 Dashboard store 同源（refreshOverview），避免与顶栏 useUsdtBalance 漂移。
  // plans 由 usePlansStore 全局缓存，无需在此重拉。
  const refreshAll = useCallback(async () => {
    await Promise.all([refreshOverview(), refreshCurrentSubscription()]);

    const overview = useDashboardStore.getState().overview;
    // Decimal-safe parse from API string; falls back silently when missing.
    const d = tryM(overview?.balance?.availableUsdt);
    if (d !== null) {
      setUserBalance(d.toNumber());
    }
  }, [refreshCurrentSubscription, refreshOverview]);

  /**
   * 仅刷新 USDT 可用余额（与 Dashboard `refreshOverview` 同源，不连带刷 current-subscription）。
   * - 用于开支付弹窗时拉取最新余额
   * - 用于 handlePayment Step 1 在算 deficit 前同步 state
   * 返回最新 balance（数字）；接口失败时返回当前内存里的 userBalance（兜底为 0）
   */
  const refreshBalance = useCallback(async (): Promise<number> => {
    setIsRefreshingBalance(true);
    try {
      await refreshOverview();
      const raw = useDashboardStore.getState().overview?.balance?.availableUsdt;
      const d = tryM(raw);
      if (d !== null) {
        const v = d.toNumber();
        setUserBalance(v);
        return v;
      }
      return Number.isFinite(userBalance) ? userBalance : 0;
    } finally {
      setIsRefreshingBalance(false);
    }
  }, [refreshOverview, userBalance]);

  useEffect(() => {
    if (!userInfo) return;
    void refreshAll();
  }, [userInfo, refreshAll]);

  useEffect(() => {
    if (billingCycle === "yearly" && !hasYearlyPlans) {
      setBillingCycle("monthly");
    }
  }, [billingCycle, hasYearlyPlans]);

  // 组件卸载时停止任何在跑的轮询
  useEffect(() => {
    return () => {
      cancelPollRef.current = true;
    };
  }, []);

  // 派生 UI 用的 plans 数据
  const plans = useMemo(() => {
    if (subscriptionPlans.length === 0) return [];

    const enabledPlans = subscriptionPlans.filter((plan) => {
      if (plan.isFree) return true;
      return billingCycle === "yearly" ? plan.yearlyEnabled : plan.monthlyEnabled;
    });

    // 选第一个非 free、且不是当前计划的作为推荐项；否则取第一个非 free
    const recommendedId = (() => {
      const nonFree = enabledPlans.filter((p) => !p.isFree);
      const notCurrent = nonFree.find(
        (p) => !currentPlanIdStr || String(p.id) !== currentPlanIdStr
      );
      return (notCurrent ?? nonFree[0])?.id ?? null;
    })();

    return enabledPlans.map((plan) => {
      const priceMicroField =
        billingCycle === "yearly"
          ? plan.yearlyPriceUsdtMicro
          : plan.monthlyPriceUsdtMicro;
      const microD = tryM(priceMicroField as number | string | undefined);
      const priceMicro = microD === null ? 0 : microD.toNumber();
      const priceUsdtRaw = resolveSubscriptionPlanPriceUsdt(plan, billingCycle);
      return {
        id: plan.id,
        apiPlanId: plan.id,
        name: plan.name,
        description: `${(plan.monthlyCredits || 0).toLocaleString()} ${t(
          "common.credits"
        )}`,
        price: formatSubscriptionPlanPriceDisplay(plan, billingCycle),
        // String form (Decimal-safe). Treat as the canonical price for any
        // arithmetic / comparison; never feed it to `Number(...)` again.
        priceNumber: priceUsdtRaw,
        priceMicro,
        points: (plan.monthlyCredits || 0).toLocaleString(),
        isFree: plan.isFree,
        recommended: plan.id === recommendedId,
        tag:
          plan.id === recommendedId ? t("recharge.plans.tagPopular") : undefined,
        features: [
          `${(plan.monthlyCredits || 0).toLocaleString()} ${t(
            "common.credits"
          )} / ${t("recharge.month")}`,
          t("recharge.features.instantActivation"),
        ],
      };
    });
  }, [subscriptionPlans, currentPlanIdStr, t, billingCycle]);

  // 默认选中：优先推荐项，其次第一个
  useEffect(() => {
    if (selectedPlanId || plans.length === 0) return;
    const defaultPlan = plans.find((p) => p.recommended) ?? plans[0];
    if (defaultPlan) setSelectedPlanId(defaultPlan.id);
  }, [plans, selectedPlanId]);

  useEffect(() => {
    if (!selectedPlanId || plans.length === 0) return;
    if (plans.some((plan) => plan.id === selectedPlanId)) return;
    const defaultPlan = plans.find((p) => p.recommended) ?? plans[0];
    setSelectedPlanId(defaultPlan?.id ?? null);
  }, [plans, selectedPlanId]);

  // 当前选中的 plan 数据（用于弹窗）
  const selectedPlanData = useMemo(
    () => plans.find((p) => p.id === selectedPlanId) ?? null,
    [plans, selectedPlanId]
  );

  /**
   * 选中计划相对于当前订阅的变更类型。
   *
   * 若选中即为当前计划（理论上 UI 已置灰拦截，但兜底返回 'subscribe'），
   * deriveChangeKind 仍能给出合理结果，handleSubscription 入口会再次校验。
   */
  const selectedChangeKind = useMemo<ChangeKind | null>(() => {
    if (!selectedPlanData) return null;
    return deriveChangeKind(
      currentSubscription?.subscriptionState,
      currentPriceMicro,
      selectedPlanData.priceMicro,
      selectedPlanData.isFree
    );
  }, [
    selectedPlanData,
    currentSubscription?.subscriptionState,
    currentPriceMicro,
  ]);

  // 点击 Subscribe：开弹窗后先 await 刷新余额（与顶栏同源），余额区骨架结束后再展示金额与主按钮文案
  //
  // 入口校验（按 yaml Subscription.subscriptionState 语义）：
  //  - 选中当前计划：兜底拦截（UI 已 disable）
  //  - paid_pending_change（即已有 pendingPlan，下周期会自动切换）：一刀切禁用，
  //    不再区分降级 / 升级覆盖 / 预扣续费——不允许本周期内再次变更订阅计划
  //  - 其他状态（free_active / free_past_due / paid_active）→ 直接放行
  const handleSubscription = useCallback(
    async (planId: PlanId) => {
      if (currentPlanIdStr && String(planId) === currentPlanIdStr) {
        toast.info(t("recharge.tooltipCurrentPlan"));
        return;
      }
      if (hasPendingChange) {
        toast.warning(t("recharge.toastPendingChangeLocked"));
        return;
      }
      setSelectedPlanId(planId);
      setIsPaymentModalOpen(true);
      setIsPaymentModalBalanceReady(false);
      try {
        await refreshBalance();
      } finally {
        setIsPaymentModalBalanceReady(true);
      }
    },
    [refreshBalance, currentPlanIdStr, hasPendingChange, t]
  );

  /**
   * 余额不足时补差额：使用和 RechargeModal 一致的"approve + 合约 deposit"流程。
   *
   * 之所以必须走合约 deposit 方法（而不是裸 ERC20 transfer），是因为后端 Deposit
   * Listener 监听的是合约自己发出的 `Deposited(user, orderNo, token, amount)`
   * 事件（见 abi/deposit.json）；裸 transfer 到合约地址后端识别不到，资金会被
   * "黑洞"。
   *
   * 流程（每个子阶段通过 `onStep` 回调通知 UI 切换进度 modal 文案）：
   *   1. creatingOrder       → POST /deposit/orders 拿 orderNo / contractAddress / tokenAddress / tokenDecimals
   *   2. switchingChain      → 钱包切链（可能弹 wallet_switchEthereumChain）
   *   3. checkingAllowance   → eth_call 查 ERC20 allowance
   *   4. 若 allowance 不足：
   *      approving           → eth_sendTransaction approve（等钱包签名）
   *      approveConfirming   → 等 receipt 上链
   *   5. submittingDeposit   → eth_sendTransaction deposit(orderNo, tokenAddress, amountUnits)
   *                            广播成功即 return { ok: true, orderId }
   *
   * 用户在任一签名环节拒绝 → toast.info 提示，return { ok: false }。
   * 后端保留 pending 订阅订单，用户可后续重试。
   */
  const sendBscUsdtDeposit = useCallback(
    async (
      deficit: string,
      onStep: (s: FlowStep) => void,
      signal: AbortSignal
    ): Promise<{ ok: true; orderId: string } | { ok: false }> => {
      if (!provider) {
        toast.error(t("wallet.errorNoProvider"));
        return { ok: false };
      }

      // Fail fast on a dead provider (disabled / invalidated extension) before
      // any blocking wallet step.
      try {
        await probeWalletAlive(provider);
      } catch (error) {
        toast.error(t(getWalletErrorMessageKey(error)));
        return { ok: false };
      }

      // 金额合法性校验：与 yaml CreateDepositOrderRequest.amount 保持一致。
      // Decimal-safe: avoids float drift around 0 / oversized strings.
      const amount = AMOUNT_PATTERN.test(deficit) ? deficit : "0";
      if (lteUsdt(amount, "0")) {
        toast.error(t("dashboard.toastAmountInvalid"));
        return { ok: false };
      }

      // 1. 启用链列表（与 App / store 共用；已成功拉取过则不再打 `/deposit/blockchains`）
      await useDepositBlockchainsStore.getState().refreshBlockchains();
      const { status: chainLoadStatus, blockchains: chainsFromStore } =
        useDepositBlockchainsStore.getState();
      if (chainLoadStatus !== "success" || !chainsFromStore[0]) {
        toast.error(t("dashboard.toastRechargePlanFail"));
        if (chainLoadStatus !== "success") {
          console.error(
            "[Recharge] refreshBlockchains did not succeed:",
            chainLoadStatus
          );
        }
        return { ok: false };
      }
      const chain = chainsFromStore[0];
      if (!chain) {
        toast.error(t("dashboard.toastRechargePlanFail"));
        return { ok: false };
      }
      const chainConfigId = Number(chain.id);
      if (!Number.isFinite(chainConfigId)) {
        toast.error(t("dashboard.toastRechargePlanFail"));
        return { ok: false };
      }

      // 2. 创建 deposit 订单
      onStep("creatingOrder");
      const orderRes = await createDepositOrder({ amount, chainConfigId });
      if (!orderRes.ok) {
        toast.error(orderRes.error.message || t("recharge.toastSubscribeFail"));
        return { ok: false };
      }
      const {
        orderId,
        orderNo,
        tokenAddress,
        tokenDecimals,
        contractAddress,
        chainId,
      } = orderRes.data;
      if (!orderId || !orderNo || !tokenAddress || !contractAddress) {
        toast.error(t("recharge.toastSubscribeFail"));
        return { ok: false };
      }

      if (!Number.isInteger(tokenDecimals) || tokenDecimals < 0) {
        console.warn(`${DEPOSIT_FLOW} invalid tokenDecimals`, tokenDecimals);
        toast.error(t("recharge.toastSubscribeFail"));
        return { ok: false };
      }

      let numericChainId: number | null = null;
      if (
        chainId !== undefined &&
        chainId !== null &&
        String(chainId) !== ""
      ) {
        const n = Number(chainId);
        if (!Number.isFinite(n)) {
          console.warn(`${DEPOSIT_FLOW} invalid chainId from order`, chainId);
          toast.error(t("recharge.toastSubscribeFail"));
          return { ok: false };
        }
        numericChainId = n;
      }

      console.info(`${DEPOSIT_FLOW} order created`, {
        orderId,
        orderNo,
        chainId: numericChainId,
        tokenDecimals,
        contract: shortHexAddr(contractAddress),
        token: shortHexAddr(tokenAddress),
      });

      try {
        // 3. 拿钱包账户
        const accounts = (await raceWithAbort(
          provider.request({
            method: "eth_requestAccounts",
          }),
          signal
        )) as string[];
        if (!accounts || accounts.length === 0) {
          toast.error(t("dashboard.toastWalletNotReady"));
          return { ok: false };
        }
        const from = accounts[0];

        // 4. 切链（必要时弹 wallet_addEthereumChain）
        if (numericChainId !== null) {
          onStep("switchingChain");
          console.info(`${DEPOSIT_FLOW} switchOrAddChain →`, numericChainId);
          await raceWithAbort(
            switchOrAddChain(provider, numericChainId, {
              chainName: chain.chainName,
              iconUrl: chain.chainLogoUrl,
            }),
            signal
          );
          console.info(`${DEPOSIT_FLOW} switchOrAddChain ok`);
        }

        // 5. allowance 检查
        onStep("checkingAllowance");
        const amountUnits = parseUnits(amount, tokenDecimals);
        const allowanceData = erc20Interface.encodeFunctionData("allowance", [
          from,
          contractAddress,
        ]);
        const rawAllowance = (await raceWithAbort(
          provider.request({
            method: "eth_call",
            params: [{ to: tokenAddress, data: allowanceData }, "latest"],
          }),
          signal
        )) as string;
        const allowanceResult = erc20Interface.decodeFunctionResult(
          "allowance",
          rawAllowance
        );
        const currentAllowance = allowanceResult[0] as bigint;

        const needApprove = currentAllowance < amountUnits;
        console.info(`${DEPOSIT_FLOW} allowance`, {
          currentAllowance: currentAllowance.toString(),
          amountUnits: amountUnits.toString(),
          needApprove,
        });

        // 6. 不足则先 approve(MaxUint256) 并等 receipt 上链
        if (needApprove) {
          onStep("approving");
          const approveData = erc20Interface.encodeFunctionData("approve", [
            contractAddress,
            MaxUint256,
          ]);
          console.info(`${DEPOSIT_FLOW} eth_sendTransaction approve →`, {
            to: shortHexAddr(tokenAddress),
          });
          const approveTxHash = (await raceWithAbort(
            provider.request({
              method: "eth_sendTransaction",
              params: [
                {
                  from,
                  to: tokenAddress,
                  data: approveData,
                  value: "0x0",
                },
              ],
            }),
            signal
          )) as string;
          console.info(`${DEPOSIT_FLOW} approve tx broadcast`, { approveTxHash });
          onStep("approveConfirming");
          await waitForReceipt(provider, approveTxHash, signal);
          console.info(`${DEPOSIT_FLOW} approve receipt ok`);
        }

        // 7. 合约 deposit(orderNo, token, amount) —— 必须走这里后端才能监听到 Deposited 事件
        onStep("submittingDeposit");
        const depositData = depositInterface.encodeFunctionData("deposit", [
          orderNo,
          tokenAddress,
          amountUnits,
        ]);
        console.info(`${DEPOSIT_FLOW} eth_sendTransaction deposit →`, {
          to: shortHexAddr(contractAddress),
        });
        const depositTxHash = (await raceWithAbort(
          provider.request({
            method: "eth_sendTransaction",
            params: [
              {
                from,
                to: contractAddress,
                data: depositData,
                value: "0x0",
              },
            ],
          }),
          signal
        )) as string;
        console.info(`${DEPOSIT_FLOW} deposit tx broadcast`, { depositTxHash });

        return { ok: true, orderId };
      } catch (error) {
        const err = error as { code?: number; message?: string };
        console.error("[Recharge] deposit flow error:", error);
        console.error(`${DEPOSIT_FLOW} failed`, {
          code: err.code,
          message: err.message,
        });
        const code = classifyWalletError(error);
        if (code === "cancelled") {
          toast.info(t("wallet.cancelled"));
        } else if (code === "userRejected") {
          toast.info(t("dashboard.rechargeModal.toastTxRejected"));
        } else {
          // 钱包层错误给精确提示；其它（链上 revert / 后端失败）沿用订阅失败文案。
          toast.error(
            code === "unknown"
              ? t("recharge.toastSubscribeFail")
              : t(getWalletErrorMessageKey(code))
          );
        }
        return { ok: false };
      }
    },
    [provider, t]
  );

  /**
   * 轮询 deposit 订单直到后端确认到账（status=paid）/ 过期 / 超时 / 用户取消。
   *
   * 说明：
   * - 每 3s 调一次 getDepositOrderDetail（按 id 查，权威）
   * - 超时时长 3 分钟（60 次），BSC 区块 3s，足够覆盖正常确认 + 后端轮询入账
   * - 单次请求失败继续重试；连续失败超过 MAX_CONSECUTIVE_FAILURES 次视为 "timeout" 退出，
   *   避免鉴权过期 / 服务侧持续报错时干等满 3 分钟
   * - 'aborted' 表示用户主动关闭等待弹窗：deposit 订单后端会继续走（最终仍可能到账），
   *   只是前端不再阻塞订阅
   */
  const waitForDepositOrderPaid = useCallback(
    async (
      orderId: string
    ): Promise<"paid" | "expired" | "timeout" | "aborted"> => {
      const intervalMs = 3000;
      const maxAttempts = 60;
      const MAX_CONSECUTIVE_FAILURES = 5;
      const idNum = Number(orderId);
      if (!Number.isFinite(idNum)) return "expired";
      let consecutiveFailures = 0;
      for (let i = 0; i < maxAttempts; i++) {
        if (cancelPollRef.current) return "aborted";
        if (i > 0) {
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
          if (cancelPollRef.current) return "aborted";
        }
        const res = await getDepositOrderDetail(idNum);
        if (!res.ok) {
          consecutiveFailures += 1;
          console.error(
            "[Recharge] getDepositOrderDetail failed during poll:",
            res.error
          );
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
            console.warn(
              "[Recharge] waitForDepositOrderPaid aborted due to consecutive API failures"
            );
            return "timeout";
          }
          continue;
        }
        consecutiveFailures = 0;
        if (res.data.status === "paid") return "paid";
        if (res.data.status === "expired") return "expired";
        // 'pending' 继续等
      }
      return "timeout";
    },
    []
  );

  /**
   * 轮询 pending 订阅订单直到后端确认激活（status=paid）或终态（expired/cancelled）或超时。
   *
   * 说明：
   * - 每 3s 调一次 listSubscriptionOrders 列表（后端未提供按 id 查订单的接口），
   *   在列表里匹配 orderId
   * - 超时时长 3 分钟（60 次），BSC 区块 3s，足够覆盖正常确认 + 后端轮询入账
   * - 单次请求失败继续重试；连续失败超过 MAX_CONSECUTIVE_FAILURES 次视为 "timeout" 退出，
   *   避免鉴权过期 / 服务侧持续报错时干等满 3 分钟
   * - 任一路径（paid/expired/cancelled/timeout/cancel）都干净退出
   */
  const waitForSubscriptionOrderPaid = useCallback(
    async (
      orderId: string
    ): Promise<"paid" | "expired" | "cancelled" | "timeout" | "aborted"> => {
      const intervalMs = 3000;
      const maxAttempts = 60;
      const MAX_CONSECUTIVE_FAILURES = 5;
      let consecutiveFailures = 0;
      for (let i = 0; i < maxAttempts; i++) {
        if (cancelPollRef.current) return "aborted";
        // 首轮立即查，其余间隔 3s
        if (i > 0) {
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
          if (cancelPollRef.current) return "aborted";
        }
        const res = await getSubscriptionOrders({ page: 1, limit: 20 });
        if (!res.ok) {
          consecutiveFailures += 1;
          console.error(
            "[Recharge] getSubscriptionOrders failed during poll:",
            res.error
          );
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
            console.warn(
              "[Recharge] waitForSubscriptionOrderPaid aborted due to consecutive API failures"
            );
            return "timeout";
          }
          continue;
        }
        consecutiveFailures = 0;
        const order = res.data.orders.find(
          (o) => String(o.id) === String(orderId)
        );
        if (!order) {
          // pending 订单可能因分页被挤到后面——保守处理：列表里暂时找不到时继续等
          continue;
        }
        if (order.status === "paid") return "paid";
        if (order.status === "expired") return "expired";
        if (order.status === "cancelled") return "cancelled";
        // status === 'pending' 继续等
      }
      return "timeout";
    },
    []
  );

  /**
   * 订阅订单轮询结果的统一收口（pending 复用 / 兜底分支共用）。
   *
   * paid 分支按 changeKind 选不同的成功 toast：
   * - subscribe → 初次订阅成功
   * - upgrade   → 升级成功（含「积分继承」提示）
   * - downgrade / downgrade_to_free → 降级成功（下周期生效）
   */
  const handleSubscriptionOutcome = useCallback(
    async (
      outcome: "paid" | "expired" | "cancelled" | "timeout" | "aborted",
      toastId: string,
      kind: ChangeKind
    ) => {
      switch (outcome) {
        case "paid":
          toast.success(successToastKey(kind, t), { id: toastId });
          await refreshAll();
          break;
        case "expired":
          toast.error(t("recharge.toastSubscribeOrderExpired"), { id: toastId });
          await refreshAll();
          break;
        case "cancelled":
          toast.error(t("recharge.toastSubscribeOrderCancelled"), {
            id: toastId,
          });
          await refreshAll();
          break;
        case "timeout":
          toast.info(t("recharge.toastSubscribeConfirmTimeout"), {
            id: toastId,
          });
          await refreshAll();
          break;
        case "aborted":
          toast.info(t("recharge.toastSubscribePending"), { id: toastId });
          break;
      }
    },
    [refreshAll, t]
  );

  /**
   * 弹窗里的 Pay 按钮：统一「余额够直接订阅，不够先充值到账再订阅」单一路径。
   *
   * 按 selectedChangeKind 选择最终调用的接口：
   * - subscribe                       → POST /subscriptions/subscribe（免费 → 付费）
   * - upgrade / downgrade / downgrade_to_free → POST /subscriptions/change
   *
   * 流程（不区分是否存在历史 pending 订阅订单；若有旧 pending，后端按 yaml
   * 约定会在新请求到来时自动将其标记为 expired 并创建新订单）：
   *
   *  Step 1: refreshBalance（与 Dashboard refreshOverview 同源，避免 stale state 漂移）
   *  Step 2: 计算 deficit = max(0, target − balance)
   *          - downgrade_to_free：target=0，deficit 必为 0
   *          - 其他：target = selectedPlanData.priceNumber
   *  Step 3: deficit > 0 → 链上 deposit + 等到账 paid + refreshAll
   *  Step 4: sign + subscribe / change，按响应分支处理
   *          - subscribe.activated   → toastSubscribeOk
   *          - change.activated      → toastUpgradeOk（升级立即生效，含积分继承）
   *          - change.scheduled      → toastDowngradeOk（降级下周期生效）
   *          - 任意 pending_order    → 并发扣费 / 余额漂移的边缘 case，
   *            走 waitForSubscriptionOrderPaid 轮询兜底
   */
  const handlePayment = useCallback(async () => {
    if (isProcessing) return;
    if (!selectedPlanData) {
      toast.error(t("recharge.toastNoPlanSelected"));
      return;
    }
    const kind = selectedChangeKind ?? "subscribe";

    // subscribe 接口要 integer，change 接口要 string；分别校验
    const planIdStr = String(selectedPlanData.apiPlanId);
    const planIdNum = Number(selectedPlanData.apiPlanId);
    if (kind === "subscribe" && !Number.isFinite(planIdNum)) {
      toast.error(t("recharge.toastSubscribeFail"));
      return;
    }

    setIsProcessing(true);
    // 立刻关闭确认弹窗，并切到进度 modal 的起始阶段 refreshingBalance
    setIsPaymentModalOpen(false);
    setFlowStep("refreshingBalance");
    const toastId = "subscription";
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // Step 1: refreshBalance
      const freshBalance = await refreshBalance();

      // Step 2: deficit = max(0, target − balance)
      // downgrade_to_free 不扣款；其他用 selectedPlanData.priceNumber 作为目标价。
      // Decimal-safe: avoids "9.000000000000002" style float drift on subtract.
      const target =
        kind === "downgrade_to_free" ? "0" : selectedPlanData.priceNumber;
      const deficitStr = clampNonNegative(subUsdt(target, freshBalance));

      // Step 3: deposit + wait paid（余额够 / downgrade_to_free 时跳过）
      if (isPositiveUsdt(deficitStr)) {
        cancelPollRef.current = false;
        setWaitDepositAmount(deficitStr);
        // sendBscUsdtDeposit 内部会依次 setFlowStep 为 creatingOrder / switchingChain
        // / checkingAllowance / approving / approveConfirming / submittingDeposit
        const dep = await sendBscUsdtDeposit(
          deficitStr,
          setFlowStep,
          controller.signal
        );
        if (!dep.ok) {
          return;
        }
        toast.info(t("recharge.toastSubscribePendingOrderCreated"), {
          id: toastId,
        });
        setFlowStep("depositSubmitted");
        const out = await waitForDepositOrderPaid(dep.orderId);
        switch (out) {
          case "paid":
            // 充值到账 → 刷新余额 → 进入订阅签名阶段
            await refreshAll();
            break;
          case "expired":
            toast.error(t("recharge.toastDepositExpired"), { id: toastId });
            await refreshAll();
            return;
          case "timeout":
            toast.info(t("recharge.toastDepositTimeout"), { id: toastId });
            await refreshAll();
            return;
          case "aborted":
            toast.info(t("recharge.toastSubscribePending"), { id: toastId });
            return;
        }
      }

      // Step 4: sign + 调接口
      setFlowStep("signingSubscription");
      const sig = await raceWithAbort(getWalletSignature(), controller.signal);
      if (!sig) {
        toast.error(t("recharge.toastSignatureFailed"), { id: toastId });
        return;
      }

      setFlowStep("callingSubscription");

      if (kind === "subscribe") {
        const res = await subscribePlan({
          planId: planIdNum,
          billingCycle,
          signature: sig.signature,
        });
        if (!res.ok) {
          toast.error(res.error.message || t("recharge.toastSubscribeFail"), {
            id: toastId,
          });
          return;
        }
        if (res.data.result === "activated") {
          toast.success(t("recharge.toastSubscribeOk"), { id: toastId });
          await refreshAll();
          return;
        }
        // 兜底：理论不应到这（deposit 已 paid 且余额已刷）。
        // 万一后端仍返回 pending_order（并发扣费 / 余额漂移），用 waitForSubscriptionOrderPaid 兜底。
        cancelPollRef.current = false;
        setFlowStep("waitingSubscription");
        const fallbackOut = await waitForSubscriptionOrderPaid(res.data.order.id);
        await handleSubscriptionOutcome(fallbackOut, toastId, kind);
        return;
      }

      // upgrade / downgrade / downgrade_to_free → changeSubscription
      const res = await changeSubscription({
        planId: planIdStr,
        billingCycle,
        signature: sig.signature,
      });
      if (!res.ok) {
        toast.error(res.error.message || t("recharge.toastSubscribeFail"), {
          id: toastId,
        });
        return;
      }

      if (res.data.result === "activated") {
        // 升级立即生效（含积分继承）
        toast.success(t("recharge.toastUpgradeOk"), { id: toastId });
        await refreshAll();
        return;
      }
      if (res.data.result === "scheduled") {
        // 降级下周期生效（含降级到付费 / 降级到 free）
        toast.success(t("recharge.toastDowngradeOk"), { id: toastId });
        await refreshAll();
        return;
      }
      // result === 'pending_order'：兜底等订单 paid
      cancelPollRef.current = false;
      setFlowStep("waitingSubscription");
      const changeFallbackOut = await waitForSubscriptionOrderPaid(
        res.data.order.id
      );
      await handleSubscriptionOutcome(changeFallbackOut, toastId, kind);
    } catch (error) {
      console.error("[Recharge] subscribe/change error:", error);
      const code = classifyWalletError(error);
      // 用户点"取消"逃生：复位即可。
      if (code === "cancelled") {
        toast.info(t("wallet.cancelled"), { id: toastId });
        return;
      }
      // 用户在钱包中主动拒绝签名（EIP-1193 code=4001）不是"错误"，
      // 给一个友好 info toast 避免红色 error 误导用户。
      if (code === "userRejected") {
        toast.info(t("recharge.toastSignatureFailed"), { id: toastId });
        return;
      }
      // 钱包层错误给精确提示；其它（后端失败等）沿用订阅失败文案。
      toast.error(
        code === "unknown"
          ? t("recharge.toastSubscribeFail")
          : t(getWalletErrorMessageKey(code)),
        { id: toastId }
      );
    } finally {
      abortRef.current = null;
      setIsProcessing(false);
      setFlowStep("idle");
    }
  }, [
    isProcessing,
    selectedPlanData,
    selectedChangeKind,
    refreshBalance,
    getWalletSignature,
    sendBscUsdtDeposit,
    waitForDepositOrderPaid,
    waitForSubscriptionOrderPaid,
    handleSubscriptionOutcome,
    refreshAll,
    billingCycle,
    t,
  ]);

  // 用户在"等待链上确认"弹窗点击关闭：停止轮询，但保留后端订单
  // - flowStep='depositSubmitted'  ：deposit 仍会上链/入账，只是前端不再续走订阅
  // - flowStep='waitingSubscription'：pending 订阅订单后端保留，可后续重试
  const handleDismissWaiting = useCallback(() => {
    cancelPollRef.current = true;
    setFlowStep("idle");
  }, []);

  // 交互式钱包步骤卡住时的逃生口：abort 进行中的请求，让 catch 复位 UI。
  const handleCancelWalletStep = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const isEmpty = !isLoadingPlans && plans.length === 0;

  /** 当前网络展示严格来自 GET /deposit/blockchains 的第 0 项 */
  const rechargeChainDisplayName = primaryChain?.chainName ?? "";
  const rechargeChainAlt = primaryChain?.chainName ?? "";
  const isRechargeChainLoading = !primaryChain;
  const showRechargeChainLogo =
    primaryChain != null && hasValidChainLogoUrl(primaryChain.chainLogoUrl);

  return (
    <div className="flex flex-col space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0 mx-[0px] mt-[0px] mb-[32px]">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {t("recharge.title")}
          </h1>
          <p className="text-slate-500 dark:text-[#606566] text-[14px] mx-[0px] mt-[4px] mb-[0px]">
            {t("recharge.subtitle")}
          </p>
        </div>
      </div>

      {/* Billing Cycle Switcher */}
      {!isEmpty && (
        <div className="flex justify-center mb-4 shrink-0">
          <div className="inline-flex bg-slate-50 dark:bg-[#141517] rounded-full p-1 border border-slate-200 dark:border-[#D2F0E1]/5">
            <button
              type="button"
              onClick={() => setBillingCycle("monthly")}
              className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${
                billingCycle === "monthly"
                  ? "bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12]"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
              }`}
            >
              {t("recharge.monthly")}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!hasYearlyPlans) {
                  toast.info(t("recharge.yearlyUnavailable"));
                  return;
                }
                setBillingCycle("yearly");
              }}
              disabled={!hasYearlyPlans}
              className={`px-6 py-2 rounded-full text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                billingCycle === "yearly"
                  ? "bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12]"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
              }`}
            >
              {t("recharge.yearly")}
            </button>
          </div>
        </div>
      )}

      {/* 有待生效计划时的说明横幅：告诉用户下周期将切换到哪个计划、何时生效，
          避免用户对整个 plan 列表被灰化产生困惑 */}
      {hasPendingChange && currentSubscription?.pendingPlan && (
        <div className="mx-auto w-full max-w-5xl mb-6 px-5 py-4 rounded-2xl bg-amber-50 dark:bg-amber-500/5 border border-amber-200 dark:border-amber-500/20 flex items-start gap-3">
          <Info className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-amber-900 dark:text-amber-200">
              {t("recharge.pendingChangeBanner.title")}
            </p>
            <p className="text-xs text-amber-800 dark:text-amber-300 mt-1 leading-relaxed">
              {t("recharge.pendingChangeBanner.description", {
                planName: currentSubscription.pendingPlan.planName,
                date: formatDateTime(
                  currentSubscription.pendingPlan.effectiveAt ?? 0,
                  false
                ),
              })}
            </p>
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex flex-col md:flex-row gap-6 mx-auto w-full items-stretch pb-4">
        {isLoadingPlans ? (
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            <PlanCardSkeleton />
            <PlanCardSkeleton />
            <PlanCardSkeleton />
            <PlanCardSkeleton />
          </div>
        ) : isEmpty ? (
          <div className="flex-1 flex items-center justify-center pt-12">
            <div className="text-center max-w-md mx-auto p-8 bg-slate-50 dark:bg-[#141517] rounded-2xl border border-slate-200 dark:border-[#D2F0E1]/5">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-50 dark:from-[#00FFA3]/10 dark:to-[#00FFA3]/5 flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-emerald-600 dark:text-[#00FFA3]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                {t("recharge.noPlansTitle")}
              </h3>
              <p className="text-sm text-slate-500 dark:text-[#606566] leading-relaxed mb-6">
                {t("recharge.noPlansDescription")}
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => void refreshPlans()}
                  className="px-6 py-2.5 bg-slate-200 dark:bg-[#1a1b1d] text-slate-900 dark:text-white rounded-full font-bold hover:bg-slate-300 dark:hover:bg-[#252628] transition-all hover:scale-105 active:scale-95"
                >
                  {t("common.refresh")}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="px-6 py-2.5 border-2 border-slate-300 dark:border-[#D2F0E1]/5 text-slate-700 dark:text-slate-300 rounded-full font-bold hover:border-emerald-500 dark:hover:border-[#00FFA3] hover:text-emerald-600 dark:hover:text-[#00FFA3] transition-all"
                >
                  {t("common.backToHome")}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {plans.map((plan) => {
              // 当前订阅按 planId 严格比较（旧实现按 planName 易被名称重复 / 空值误伤）
              const isCurrentPlan =
                !!currentPlanIdStr && String(plan.apiPlanId) === currentPlanIdStr;
              // 免费计划：用户登录即默认包含，不需要"订阅"动作；
              // 当当前订阅本身已是免费计划时，优先标记为"当前计划"，避免 badge 重复。
              const isDefaultFree = !!plan.isFree && !isCurrentPlan;
              // 已有下周期生效的 pendingPlan → 本周期内不允许再次变更，所有非当前计划
              // 一律灰出。当前计划仍显 "Current Plan" 徽章（保持视觉突出）。
              const isLockedByPending = hasPendingChange && !isCurrentPlan;
              const isDisabled =
                isCurrentPlan || isDefaultFree || isLockedByPending;
              const isSelected = selectedPlanId === plan.id;

              return (
                <div
                  key={plan.id}
                  role="button"
                  tabIndex={isDisabled ? -1 : 0}
                  onClick={() => !isDisabled && setSelectedPlanId(plan.id)}
                  onKeyDown={(e) => {
                    if (isDisabled) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedPlanId(plan.id);
                    }
                  }}
                  className={`relative flex flex-col p-6 bg-slate-50 dark:bg-[#141517] rounded-2xl transition-all ${
                    isDisabled
                      ? "opacity-60 cursor-not-allowed border-2 border-slate-400 dark:border-slate-600"
                      : `cursor-pointer ${
                          isSelected
                            ? "border-2 border-emerald-500 ring-2 ring-emerald-500/20 dark:border-[#00FFA3] dark:ring-[#00FFA3]/10 z-10"
                            : "border border-slate-200 dark:border-[#D2F0E1]/5 hover:border-emerald-500 dark:hover:border-[#00FFA3]"
                        }`
                  }`}
                >
                  {isCurrentPlan && (
                    <div className="absolute top-0 transform -translate-y-1/2 left-1/2 -translate-x-1/2">
                      <span className="bg-slate-600 dark:bg-slate-700 text-white px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide">
                        {t("recharge.currentPlan")}
                      </span>
                    </div>
                  )}
                  {isDefaultFree && (
                    <div className="absolute top-0 transform -translate-y-1/2 left-1/2 -translate-x-1/2">
                      <span className="bg-slate-600 dark:bg-slate-700 text-white px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide">
                        {t("recharge.defaultPlan")}
                      </span>
                    </div>
                  )}
                  {plan.recommended && !isDisabled && plan.tag && (
                    <div className="absolute top-0 transform -translate-y-1/2 left-1/2 -translate-x-1/2">
                      <span className="bg-gradient-to-r from-emerald-500 to-emerald-600 dark:from-[#00FFA3] dark:to-emerald-700 text-white px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide flex items-center gap-1 border border-emerald-400 dark:border-[#00FFA3]/50">
                        <Star className="w-2.5 h-2.5 fill-white" />
                        {plan.tag}
                      </span>
                    </div>
                  )}

                  <div className="mb-5 shrink-0">
                    <div className="flex justify-between items-start">
                      <h2 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                        {plan.name}
                      </h2>
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                          isSelected && !isDisabled
                            ? "border-emerald-500 bg-emerald-500 dark:border-[#00FFA3] dark:bg-[#00FFA3]"
                            : "border-slate-300 dark:border-[#D2F0E1]/5"
                        }`}
                      >
                        {isSelected && !isDisabled && (
                          <Check className="w-3 h-3 text-white dark:text-[#030E12] stroke-[3]" />
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-[#606566] mt-1">
                      {plan.description}
                    </p>
                  </div>

                  <div className="mb-5 shrink-0">
                    <div className="flex items-baseline gap-1.5 flex-wrap">
                      <span className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                        {plan.price}
                      </span>
                      <span className="text-sm font-bold text-slate-500 dark:text-[#606566]">
                        {t("common.usdt")}
                      </span>
                      <span className="text-sm font-bold text-slate-500 dark:text-[#606566]">
                        / {billingCycle === "yearly" ? t("recharge.year") : t("recharge.month")}
                      </span>
                    </div>
                    <div className="mt-4 bg-emerald-50/50 dark:bg-[#00FFA3]/5 border border-emerald-100 dark:border-[#00FFA3]/10 rounded-xl p-3 flex items-center justify-between">
                      <span className="text-xs font-bold text-emerald-900 dark:text-emerald-300 uppercase tracking-wider">
                        {t("common.credits")}
                      </span>
                      <span className="text-sm font-extrabold text-emerald-600 dark:text-[#00FFA3] flex items-center gap-1">
                        <Zap className="w-4 h-4 fill-emerald-500/20 dark:fill-[#00FFA3]/20" />
                        {plan.points}
                      </span>
                    </div>
                  </div>

                  <ul className="space-y-3 flex-1 text-sm font-medium mb-4">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start group">
                        <div className="flex-shrink-0 mt-0.5">
                          <Check className="w-4 h-4 text-emerald-600 dark:text-[#00FFA3] group-hover:scale-110 transition-transform" />
                        </div>
                        <p className="ml-3 text-sm text-slate-700 dark:text-slate-300 leading-tight">
                          {feature}
                        </p>
                      </li>
                    ))}
                    <li className="flex items-start group shrink-0">
                      <div className="flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-emerald-600 dark:text-[#00FFA3] group-hover:scale-110 transition-transform" />
                      </div>
                      <p className="ml-3 text-sm text-slate-700 dark:text-slate-300 leading-tight">
                        {t("recharge.payAsYouUsed")}
                      </p>
                    </li>
                  </ul>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isDisabled) return;
                      void handleSubscription(plan.id);
                    }}
                    disabled={isDisabled}
                    title={
                      isCurrentPlan
                        ? t("recharge.tooltipCurrentPlan")
                        : isLockedByPending
                        ? t("recharge.toastPendingChangeLocked")
                        : undefined
                    }
                    aria-disabled={isDisabled}
                    className={`w-full py-3 rounded-full font-bold transition-all flex items-center justify-center shrink-0 ${
                      isDisabled
                        ? "bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-500 cursor-not-allowed"
                        : isSelected
                        ? "bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] hover:opacity-90"
                        : "bg-transparent text-slate-700 dark:text-white border border-slate-200 dark:border-[#D2F0E1]/5 hover:bg-slate-100 dark:hover:bg-[#0E0F11]/50"
                    }`}
                  >
                    {isCurrentPlan
                      ? t("recharge.currentPlan")
                      : isLockedByPending
                      ? t("recharge.pendingChangeLockedBadge")
                      : isDefaultFree
                      ? t("recharge.defaultPlan")
                      : t("recharge.subscribeAction")}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirm Subscription Modal */}
      {isPaymentModalOpen && selectedPlanData && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-slate-50 dark:bg-[#141517] rounded-2xl border border-slate-200 dark:border-[#D2F0E1]/5 max-w-md w-full p-8 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                {t("recharge.confirmSubscription.title")}
              </h3>
              <button
                type="button"
                onClick={() => {
                  if (isProcessing) return;
                  setIsPaymentModalOpen(false);
                  setIsPaymentModalBalanceReady(false);
                }}
                disabled={isProcessing}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {(() => {
              // Decimal-safe price/balance arithmetic; avoids 0.1 + 0.2 drift
              // and oversized-balance precision loss. All downstream consumers
              // (formatUsdtBalance, comparison) accept string values.
              const planPriceStr = selectedPlanData.priceNumber;
              const balanceD = tryM(userBalance);
              const safeBalanceStr = balanceD === null ? "0" : balanceD.toFixed();
              const needsWallet = ltUsdt(safeBalanceStr, planPriceStr);
              const balanceUsedStr = lteUsdt(safeBalanceStr, planPriceStr)
                ? safeBalanceStr
                : planPriceStr;
              const walletNeededStr = clampNonNegative(
                subUsdt(planPriceStr, safeBalanceStr)
              );
              const balanceIsPositive = isPositiveUsdt(safeBalanceStr);
              const balanceIsZero = !balanceIsPositive;
              const planPriceIsPositive = isPositiveUsdt(planPriceStr);

              return (
                <div className="space-y-4">
                  <div className="bg-white dark:bg-[#0E0F11] rounded-xl p-4 border border-slate-200 dark:border-[#D2F0E1]/5">
                    <div className="flex justify-between items-center mb-2 gap-2">
                      <span className="text-sm font-bold text-slate-500 dark:text-[#606566]">
                        {t("recharge.confirmSubscription.plan")}
                      </span>
                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        <span className="text-sm font-bold text-slate-900 dark:text-slate-200">
                          {selectedPlanData.name}
                        </span>
                        {(selectedChangeKind === "upgrade" ||
                          selectedChangeKind === "subscribe") && (
                          // 升级 / 初次订阅（Free → 付费）：显示「立即生效」标签
                          // + 感叹号 tooltip（复用积分继承提示，与升级对称展示）
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-500/15 text-[11px] font-bold text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30">
                            {t("recharge.tagEffectiveImmediately")}
                            <InfoTooltip
                              content={t("recharge.upgradeCreditsInheritHint")}
                              iconSize={12}
                              triggerClassName="text-emerald-700 dark:text-emerald-300"
                              ariaLabel={t("recharge.upgradeCreditsInheritHint")}
                            />
                          </span>
                        )}
                        {(selectedChangeKind === "downgrade" ||
                          selectedChangeKind === "downgrade_to_free") && (
                          // 降级 / 降级到 Free：显示「下周期生效」琥珀标签 + 生效日期 tooltip
                          // 日期来自 currentSubscription.periodEndAt（本周期末 = 下周期开始）
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/15 text-[11px] font-bold text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30">
                            {t("recharge.tagEffectiveNextCycle")}
                            <InfoTooltip
                              content={t("recharge.downgradeEffectiveHint", {
                                date: currentSubscription?.periodEndAt
                                  ? formatDateTime(
                                      currentSubscription.periodEndAt,
                                      false
                                    )
                                  : "—",
                              })}
                              iconSize={12}
                              triggerClassName="text-amber-700 dark:text-amber-300"
                              ariaLabel={t("recharge.tagEffectiveNextCycle")}
                            />
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-bold text-slate-500 dark:text-[#606566]">
                        {t("recharge.confirmSubscription.totalPrice")}
                      </span>
                      <span className="text-sm font-bold text-slate-900 dark:text-slate-200">
                        {formatUsdtBalance(selectedPlanData.price)}{" "}
                        {t("common.usdt")}
                      </span>
                    </div>
                    <div className="h-px bg-slate-200 dark:bg-[#D2F0E1]/5 my-2"></div>
                    {!isPaymentModalBalanceReady ? (
                      <div className="space-y-3 pt-1" aria-busy="true">
                        <div className="flex justify-end">
                          <div className="h-4 w-28 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                        </div>
                        <div className="flex justify-end">
                          <div className="h-4 w-36 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                        </div>
                        <div className="flex justify-end">
                          <div className="h-3 w-32 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center mb-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-bold text-slate-500 dark:text-[#606566]">
                              {t("recharge.confirmSubscription.yourBalance")}
                            </span>
                            <button
                              type="button"
                              onClick={() => void refreshBalance()}
                              disabled={isRefreshingBalance || isProcessing}
                              aria-label={t("common.refresh")}
                              title={t("common.refresh")}
                              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <RefreshCw
                                className={`w-3.5 h-3.5 ${
                                  isRefreshingBalance ? "animate-spin" : ""
                                }`}
                              />
                            </button>
                          </div>
                          <span
                            className={`text-sm font-bold flex items-center gap-1.5 ${
                              balanceIsPositive
                                ? "text-emerald-600 dark:text-[#00FFA3]"
                                : "text-slate-400 dark:text-[#606566]"
                            }`}
                          >
                            {isRefreshingBalance && (
                              <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400 dark:text-[#606566]" />
                            )}
                            {formatUsdtBalance(safeBalanceStr)} {t("common.usdt")}
                          </span>
                        </div>
                        {needsWallet && isPositiveUsdt(balanceUsedStr) && (
                          <>
                            <div className="flex justify-between mb-1">
                              <span className="text-xs text-slate-500 dark:text-[#606566]">
                                {t("recharge.confirmSubscription.fromBalance")}
                              </span>
                              <span className="text-xs font-bold text-emerald-600 dark:text-[#00FFA3]">
                                -{formatUsdtBalance(balanceUsedStr)}{" "}
                                {t("common.usdt")}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-xs text-orange-600 dark:text-orange-400">
                                {t("recharge.confirmSubscription.fromWallet")}
                              </span>
                              <span className="text-xs font-bold text-orange-600 dark:text-orange-400">
                                -{formatUsdtBalance(walletNeededStr)}{" "}
                                {t("common.usdt")}
                              </span>
                            </div>
                          </>
                        )}
                        {balanceIsZero && planPriceIsPositive && (
                          <div className="flex justify-between">
                            <span className="text-xs text-orange-600 dark:text-orange-400">
                              {t("recharge.confirmSubscription.paymentMethod")}
                            </span>
                            <span className="text-xs font-bold text-orange-600 dark:text-orange-400">
                              {t("recharge.confirmSubscription.walletOnly")}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={handlePayment}
                    disabled={isProcessing || !isPaymentModalBalanceReady}
                    className="w-full bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] px-4 py-3 rounded-full font-bold hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {!isPaymentModalBalanceReady
                      ? t("common.loading")
                      : balanceIsZero && planPriceIsPositive
                        ? t("recharge.payButton.walletOnly", {
                            amount: formatUsdtBalance(planPriceStr),
                          })
                        : needsWallet
                          ? t("recharge.payButton.split", {
                              balance: formatUsdtBalance(balanceUsedStr),
                              wallet: formatUsdtBalance(walletNeededStr),
                            })
                          : t("recharge.payButton.fromBalance", {
                              amount: formatUsdtBalance(planPriceStr),
                            })}
                  </button>

                  {selectedChangeKind !== "upgrade" && (
                    // 升级时整块隐藏：与上方「立即生效」绿标 + 积分继承 tooltip 冲突
                    // 其他分支按语义选不同提示：
                    //  - downgrade / downgrade_to_free → noteCycle（下周期生效，与琥珀标呼应）
                    //  - subscribe（Free → 付费初次订阅）→ noteRenewal（自动续费说明）
                    <div className="bg-blue-50 dark:bg-blue-500/10 rounded-xl p-3 border border-blue-200 dark:border-blue-500/20">
                      <p className="text-xs text-blue-800 dark:text-blue-200 leading-relaxed">
                        <strong>{t("common.important")}</strong>{" "}
                        {selectedChangeKind === "downgrade" ||
                        selectedChangeKind === "downgrade_to_free"
                          ? t("recharge.noteCycle")
                          : t("recharge.noteRenewal")}
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* 订阅支付进度 Modal（单 modal，按 flowStep 驱动标题 / 描述 / 是否可关闭）
          - flowStep='idle' 不显示
          - 签名 / 创建订单 / RPC 等短阶段不显示关闭按钮，避免误中断链上交易
          - depositSubmitted / waitingSubscription 长轮询阶段显示关闭按钮，允许用户稍后再看 */}
      {flowStep !== "idle" && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-slate-50 dark:bg-[#141517] rounded-2xl border border-slate-200 dark:border-[#D2F0E1]/5 max-w-sm w-full p-8 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="relative w-16 h-16 mb-6">
                <div className="absolute inset-0 border-4 border-slate-200 dark:border-[#D2F0E1]/10 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-transparent border-t-emerald-500 dark:border-t-[#00FFA3] rounded-full animate-spin"></div>
              </div>

              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                {t(`recharge.flowStep.${flowStep}.title`)}
              </h3>

              <p className="text-sm text-slate-500 dark:text-[#606566] leading-relaxed mb-3">
                {t(`recharge.flowStep.${flowStep}.description`, {
                  amount: waitDepositAmount,
                })}
              </p>

              {isDismissibleStep(flowStep) && (
                <p className="text-xs text-slate-400 dark:text-[#606566] leading-relaxed mb-5">
                  {t("recharge.waitConfirm.hint")}
                </p>
              )}

              <div
                className={`flex items-center gap-2 bg-white dark:bg-[#0E0F11] rounded-full px-4 py-2 border border-slate-200 dark:border-[#D2F0E1]/5 ${
                  isDismissibleStep(flowStep) || isWalletWaitingStep(flowStep)
                    ? "mb-5"
                    : ""
                }`}
              >
                {isRechargeChainLoading ? (
                  <>
                    <span className="h-5 w-5 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse" />
                    <span className="h-4 w-24 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                  </>
                ) : (
                  <>
                    {showRechargeChainLogo ? (
                      <img
                        src={primaryChain!.chainLogoUrl.trim()}
                        alt={rechargeChainAlt}
                        className="w-5 h-5 rounded-full object-cover ring-1 ring-slate-200/80 dark:ring-slate-600/80"
                      />
                    ) : (
                      <span
                        className="h-5 w-5 shrink-0 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse ring-1 ring-slate-200/80 dark:ring-slate-600/80"
                        aria-hidden
                      />
                    )}
                    <span className="text-sm font-bold text-slate-900 dark:text-slate-200">
                      {rechargeChainDisplayName}
                    </span>
                  </>
                )}
              </div>

              {isDismissibleStep(flowStep) && (
                <button
                  type="button"
                  onClick={handleDismissWaiting}
                  className="w-full bg-slate-200 dark:bg-[#1a1b1d] text-slate-900 dark:text-white px-4 py-2.5 rounded-full font-bold hover:bg-slate-300 dark:hover:bg-[#252628] transition-colors text-sm"
                >
                  {t("recharge.waitConfirm.dismiss")}
                </button>
              )}

              {isWalletWaitingStep(flowStep) && (
                <button
                  type="button"
                  onClick={handleCancelWalletStep}
                  className="w-full bg-slate-200 dark:bg-[#1a1b1d] text-slate-900 dark:text-white px-4 py-2.5 rounded-full font-bold hover:bg-slate-300 dark:hover:bg-[#252628] transition-colors text-sm"
                >
                  {t("wallet.cancel")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
